import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Stack,
  TextField,
  Typography,
  FormLabel,
  Tooltip,
  Button,
  Autocomplete,
} from "@mui/material";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  checkData,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { golfTooltipHeaders } from "../../../constants/TooltipInformation";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import { removeSampleTestArray } from "../../actions/cleanup";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";

const GolfPlayerAllocation = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [playerType, setPlayerType] = useState<string[]>([]);
  const [allocationBlock, setAllocationBlock] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [GolfPlayerAllocations, setGolfPlayerAllocations] = useState<any[]>([]);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [allocationBlockList, setAllocationBlockList] = useState<string[]>([]);
  const [playerList, setPlayerList] = useState<string[]>([]);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  useEffect(() => {
    if (fieldValues) {
      setGolfPlayerAllocations(
        fieldValues[GolfPropertyEnum.playerAllocationBlock] ?? [],
      );
      setPlayerList(
        fillAutocompleteValues(
          fieldValues[GolfPropertyEnum.playerTypes],
          DNDFieldsEnum.PLAYERTYPE,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    setAllocationBlockList(
      fillAutocompleteValues(
        fieldValues?.[GolfPropertyEnum.allocationBlocks],
        DNDFieldsEnum.ALLOCATIONBLOCKNAME,
        "",
      ),
    );
  }, [fieldValues?.[GolfPropertyEnum.allocationBlocks]]);

  useEffect(() => {
    data(GolfPlayerAllocations);
    editClicked &&
      setSelectedId(
        checkData(GolfPlayerAllocations, selectedId) ? selectedId : null,
      );
  }, [GolfPlayerAllocations]);

  const buildPlayerAllocationRecords = () =>
    playerType.map((type) => ({
      "Allocation Block Code": allocationBlock,
      "Player Type": type,
    }));

  const updateGolfPlayerAllocations = () => {
    if (!allocationBlock || String(allocationBlock).trim().length === 0) {
      setAlertMessage("Allocation block is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!playerType || playerType.length === 0) {
      setAlertMessage("Player type is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    const records = buildPlayerAllocationRecords();
    setGolfPlayerAllocations((prev: any[]) => {
      const others = (prev ?? []).filter((item) => item.id !== selectedId);
      return updateData([...others, ...records]);
    });
    editGolfPlayerAllocations(null);
    setAlertMessage(
      `${GolfPropertyEnum.playerAllocationBlock} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfPlayerAllocations = () => {
    if (!allocationBlock || String(allocationBlock).trim().length === 0) {
      setAlertMessage("Allocation block is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    if (!playerType || playerType.length === 0) {
      setAlertMessage("Player type is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    const records = buildPlayerAllocationRecords();
    setGolfPlayerAllocations((prev: any[]) =>
      updateData([...(prev ?? []), ...records]),
    );
    setLastUpdatedRecord(records[0]);
    editGolfPlayerAllocations(null);
  };

  const editGolfPlayerAllocations = (list: any) => {
    setSelectedId(list?.id ?? null);
    setEditClicked(list != null);
    setAllocationBlock(list?.["Allocation Block Code"] ?? null);
    setPlayerType(
      list?.["Player Type"]
        ? String(list["Player Type"]).includes(", ")
          ? String(list["Player Type"]).split(", ")
          : [String(list["Player Type"])]
        : [],
    );
  };

  const deleteGolfPlayerAllocations = (id: number) => {
    setGolfPlayerAllocations(deleteItem(GolfPlayerAllocations ?? [], id));
  };

  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Player Type / Allocation Block
              <Tooltip
                title={tooltipComponent(
                  golfTooltipHeaders.playerAllocationBlockHeader,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editGolfPlayerAllocations(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editGolfPlayerAllocations(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack direction="column" width="100%" gap="20px">
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Allocation Block
                <Tooltip
                  title={tooltipComponent(golfTooltipHeaders.allocationBlock)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Stack direction="row" width="100%" gap="5px" alignItems="center">
                <Autocomplete
                  id="allocationBlock"
                  disablePortal
                  options={removeSampleTestArray(allocationBlockList)}
                  sx={{ width: "100%" }}
                  value={
                    allocationBlock != null ? String(allocationBlock) : null
                  }
                  disabled={!allocationBlockList?.length}
                  onChange={(_e, newValue: string | null) =>
                    setAllocationBlock(newValue)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Allocation Block"
                    />
                  )}
                />
              </Stack>
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Player Type
                <Tooltip
                  title={tooltipComponent(golfTooltipHeaders.playerType)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Stack direction="row" width="100%" gap="5px" alignItems="center">
                <Autocomplete
                  multiple
                  id="playerType"
                  disablePortal
                  options={removeSampleTestArray(playerList)}
                  sx={{ width: "100%" }}
                  value={playerType}
                  disabled={!playerList?.length}
                  onChange={(_event, newValue) => setPlayerType(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Player Type"
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId
                  ? updateGolfPlayerAllocations()
                  : addGolfPlayerAllocations()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD}
              Player Allocation
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">Allocation Block Available</Typography>
            <Stack direction="column" gap="10px">
              {GolfPlayerAllocations ? (
                <DraggableItemList
                  itemData={GolfPlayerAllocations}
                  onOrderUpdate={(order) => setGolfPlayerAllocations(order)}
                  onEdit={(info) => editGolfPlayerAllocations(info)}
                  onDelete={(id) => deleteGolfPlayerAllocations(id)}
                  code={DNDFieldsEnum.ALLOCATIONBLOCKCODE}
                  name={DNDFieldsEnum.PLAYERTYPE}
                />
              ) : (
                <Typography variant="body2">
                  No Allocation Block Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default GolfPlayerAllocation;
